import React, { FC } from 'react';
import translate from '@translate';
import { useNavigate } from 'react-router-dom';
import BodyTopBlock, { ActionType } from 'src/components/BodyTopBlock';
import { ExportTypeType, SelectRowType } from 'src/types/table';
import ExportTableAction from 'src/components/ExportTableAction';
import StoreIcon from '@mui/icons-material/Store';
import FlipIcon from '@mui/icons-material/Flip';
import AutorenewIcon from '@mui/icons-material/Autorenew';
import { ConstantsCodesObjectType, ConstantsCodeType } from '../../types/constantsCodes';
import filterUnique from '../../utils/filterUnique';
import { ClaimType } from '../../types/claim';
import { getAllowedStatuses } from './functions';
import AssignmentReturnedIcon from '@mui/icons-material/AssignmentReturned';
import { ImportIcon } from 'src/components/SkladonIcon/SmallIcons';
import { useCheckPermission } from '../../utils/checkPermission';

type TopBlockProps = {
    handleExport: (type: ExportTypeType, withProducts?: boolean) => void;
    allProviders: boolean;
    permissionAllProviders: boolean;
    permissionClaimUpdate: boolean;
    permissionClaimImport: boolean;
    setAllProviders: React.Dispatch<React.SetStateAction<boolean>>;
    reloadTableData: () => void;
    handleMassChangeStatus: (statuses: ConstantsCodeType[]) => void;
    selectedRows: SelectRowType[];
    claimsDataData?: ClaimType[];
    claimStatusCodes: ConstantsCodeType[];
    claimStatusCodesObject: ConstantsCodesObjectType;
};

const TopBlock: FC<TopBlockProps> = (props) => {
    const navigate = useNavigate();
    const permissionExportClaims = useCheckPermission('CLAIM.EXPORT.TABLE');
    const permissionExportClaimsProducts = useCheckPermission('CLAIM.EXPORT.PRODUCTS');

    const handleImport = () => {
        navigate('/claims/import/');
    };

    const getEnableMultipleActions = (): {
        ableEditStatuses: boolean;
        allowedStatuses: ConstantsCodeType[];
    } | null => {
        if (!props.selectedRows.length || !props.claimsDataData) {
            return null;
        }

        let ableEditStatuses = props.permissionClaimUpdate;

        const selectedStatuses: string[] = [];

        props.claimsDataData
            .filter((item) => props.selectedRows.includes(String(item.ID)))
            .forEach((item) => {
                if (item.STATUS) {
                    selectedStatuses.push(item.STATUS);
                }
            });

        let allowedStatuses: ConstantsCodeType[] = [];
        if (ableEditStatuses) {
            selectedStatuses.filter(filterUnique).forEach((status, index) => {
                if (index === 0) {
                    allowedStatuses = getAllowedStatuses(
                        props.claimStatusCodes,
                        props.claimStatusCodesObject,
                        { STATUS: status },
                    );
                } else {
                    const thisStatusStatuses = getAllowedStatuses(
                        props.claimStatusCodes,
                        props.claimStatusCodesObject,
                        { STATUS: status },
                    ).map((status) => status.value);

                    allowedStatuses = allowedStatuses.filter((status) => thisStatusStatuses.includes(status.value));
                }
            });
        }
        return {
            ableEditStatuses,
            allowedStatuses
        };
    };

    const ableMultipleActions = getEnableMultipleActions();
    const actions: ActionType[] = [
        {
            onClick: handleImport,
            icon: <ImportIcon />,
            tooltip: translate('claims.action.importClaims'),
            variant: 'outlined',
            hidden: !props.permissionClaimImport,
        },
        {
            node: <ExportTableAction title={translate('claims.action.exportFile')} handleExport={props.handleExport} />,
            hidden: !permissionExportClaims,
        },
        {
            onClick: () => props.setAllProviders(!props.allProviders),
            icon: <StoreIcon />,
            tooltip: translate('claims.allProviders'),
            variant: 'outlined',
            active: props.allProviders,
            hidden: !props.permissionAllProviders
        },
        {
            onClick: () => props.reloadTableData(),
            icon: <AutorenewIcon />,
            tooltip: translate('claims.action.synchronizeTable'),
            variant: 'outlined'
        },
        {
            items: [
                {
                    onClick: () =>
                        ableMultipleActions?.allowedStatuses &&
                        props.handleMassChangeStatus(ableMultipleActions?.allowedStatuses),
                    icon: <FlipIcon />,
                    label: translate('claims.action.changeStatuses'),
                    variant: 'text',
                    hidden: !ableMultipleActions?.allowedStatuses.length
                },
                {
                    node: (
                        <ExportTableAction
                            title={translate('claims.action.exportWithProducts')}
                            handleExport={(type) => props.handleExport(type, true)}
                            icon={<AssignmentReturnedIcon />}
                            textWithIcon
                        />
                    ),
                    hidden: props.allProviders || !permissionExportClaimsProducts,
                },
            ]
        }
    ];

    return <BodyTopBlock actions={actions} />;
};

export default TopBlock;
